import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { activateOnKey, interactive } from '@/components/ui/interactive';
import { clickedInteractive } from '@/lib/detail';
import { nextSortState, sortRows, type SortState } from '@/lib/table';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

export interface DataTableColumn<Row> {
  key: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  /** Supply this to make the header sortable. Returning `null` sorts the row last. */
  sortValue?: (row: Row) => string | number | null;
  headClassName?: string;
  cellClassName?: string;
}

/**
 * Sort state for a table, and the rows already in that order.
 *
 * Sorting lives here rather than inside `DataTable` because it has to happen *before*
 * pagination — a table that sorts the eight rows it was handed is sorting the page, not
 * the result set, and the top of page two would be smaller than the bottom of page one.
 * Keeping the two steps in the caller makes their order visible.
 */
export function useTableSort<Row>(
  rows: readonly Row[],
  columns: ReadonlyArray<DataTableColumn<Row>>,
  defaultSort: SortState,
  locale = 'ar',
) {
  const [sort, setSort] = useState<SortState>(defaultSort);

  const sorted = useMemo(() => {
    const column = columns.find((candidate) => candidate.key === sort.key);
    if (!column?.sortValue) return rows as Row[];
    return sortRows(rows, column.sortValue, sort.direction, locale);
  }, [rows, columns, sort, locale]);

  const toggle = (key: string) => setSort((current) => nextSortState(current, key));

  return { sort, setSort, toggle, sorted };
}

export interface DataTableProps<Row> {
  /** Rendered in the order given — see `useTableSort` for why. */
  rows: readonly Row[];
  columns: ReadonlyArray<DataTableColumn<Row>>;
  rowKey: (row: Row) => string;
  sort?: SortState;
  onSortToggle?: (key: string) => void;
  /** Rendered in place of the whole table when `rows` is empty. */
  empty?: ReactNode;
  /** Rendered below the table — where `<AdminPagination>` goes. */
  footer?: ReactNode;
  /** Accessible description of what the table lists. */
  caption: string;
  /**
   * Opens the row's record. A click on the row's own links and buttons stays theirs.
   * The row is also a tab stop that opens on Enter or Space (#145), so the whole row —
   * not only the link a column renders — is reachable from the keyboard and shows the
   * shared focus ring. It keeps its `row` role: the table stays a table to a screen
   * reader, and the link inside remains the named control.
   */
  onRowClick?: (row: Row) => void;
  /**
   * For a table inside a detail section rather than a list screen: the section's own
   * text size, no tinted head, rows aligned to the top and no side padding, so the
   * table lines up with the fields above it.
   */
  inline?: boolean;
}

const INLINE = {
  table: 'text-[12.5px]',
  header: 'border-b border-conc-100 bg-transparent font-semibold',
  head: 'py-2.5 ps-0 pe-3 font-semibold first:ps-0 last:pe-0',
  row: 'align-top',
  cell: 'py-3 ps-0 pe-3 first:ps-0 last:pe-0',
};

/**
 * The sortable admin table.
 *
 * The direction is announced through `aria-sort` on the header cell rather than only by
 * the chevron, so the state reaches a screen reader. Column headers are only sortable
 * when the column declares a `sortValue` *and* the caller wires `onSortToggle` — a
 * server-paginated caller supplies `sort` and `onSortToggle` and drops `sortValue`, and
 * nothing else about the markup changes.
 */
export function DataTable<Row>({
  rows,
  columns,
  rowKey,
  sort,
  onSortToggle,
  empty,
  footer,
  caption,
  onRowClick,
  inline = false,
}: DataTableProps<Row>) {
  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <>
      <Table className={inline ? INLINE.table : undefined}>
        <caption className="sr-only">{caption}</caption>
        <TableHeader className={inline ? INLINE.header : undefined}>
          <TableRow className="hover:bg-sideline">
            {columns.map((column) => {
              const sortable = Boolean(column.sortValue && onSortToggle);
              const active = sortable && sort?.key === column.key;

              return (
                <TableHead
                  key={column.key}
                  className={cn(inline && INLINE.head, column.headClassName)}
                  aria-sort={
                    active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => onSortToggle?.(column.key)}
                      className={cn(interactive, 'flex items-center gap-1 rounded hover:text-navy')}
                    >
                      <span>{column.header}</span>
                      {active && sort.direction === 'desc' ? (
                        <ChevronUp aria-hidden className="h-3.5 w-3.5 text-pitch" />
                      ) : (
                        <ChevronDown
                          aria-hidden
                          className={cn('h-3.5 w-3.5', active ? 'text-pitch' : 'text-conc-200')}
                        />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>

        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={rowKey(row)}
              data-clickable={onRowClick ? 'true' : undefined}
              className={cn(
                onRowClick && [interactive, 'focus-visible:ring-inset'],
                inline && INLINE.row,
              )}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={
                onRowClick
                  ? (event) => {
                      if (!clickedInteractive(event.target)) onRowClick(row);
                    }
                  : undefined
              }
              onKeyDown={onRowClick ? activateOnKey(() => onRowClick(row)) : undefined}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={cn(inline && INLINE.cell, column.cellClassName)}
                >
                  {column.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {footer}
    </>
  );
}
